import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import MaterialReactTable, { MRT_ColumnFiltersState } from "material-react-table";
import { ScorecardDataItem, ExtendedScorecardDataItem } from "api";
import { getFromLocalStorage, setToLocalStorage } from "utils";
import { LOCAL_STORAGE_KEYS } from "consts";
import { getScorecardPinnedLeftColumnIds } from "../../../../consts";
import { processScorecardData } from "../../helpers/processScorecardData";
import { useScorecardColumns } from "../../hooks/useScorecardColumns";

interface ScorecardMainTableProps {
    data: ScorecardDataItem[];
    isEditMode?: boolean;
    selectedPeriod: string;
    dateRanges: { startDate: string; endDate: string; fiscalYear?: number }[];
    isAdmin: boolean;
    selectedToggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT';
    onDataUpdate?: () => void;
    userRoles: string[];
}

export const ScorecardMainTable = ({
    data,
    isEditMode = false,
    selectedPeriod,
    dateRanges,
    isAdmin,
    selectedToggleOption,
    onDataUpdate,
    userRoles,
}: ScorecardMainTableProps) => {
    const storedColumnFilters = getFromLocalStorage(LOCAL_STORAGE_KEYS.SCORECARD_PAGE_COLUMN_FILTERS);
    const initialColumnFilters: MRT_ColumnFiltersState = Array.isArray(storedColumnFilters) ? storedColumnFilters : [];

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(initialColumnFilters);

    const processedData = useMemo(() => processScorecardData(data), [data]);
    const isTableEmpty = useMemo(() => processedData.length === 0, [processedData]);

    const currentTimeframe = selectedPeriod?.toLowerCase() || '';

    // Debounce localStorage write so we don't save on every keystroke (avoids input glitches)
    const PERSIST_DEBOUNCE_MS = 400;
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setToLocalStorage(LOCAL_STORAGE_KEYS.SCORECARD_PAGE_COLUMN_FILTERS, columnFilters);
        }, PERSIST_DEBOUNCE_MS);
        return () => window.clearTimeout(timeoutId);
    }, [columnFilters]);

    const handleDataUpdate = () => {
        if (onDataUpdate) {
            onDataUpdate();
        }
    };

    const columns = useScorecardColumns({
        selectedPeriod,
        dateRanges,
        isAdmin,
        selectedUnit: selectedToggleOption,
        isOnEditMode: isEditMode,
        isArchivedTable: false,
        currentTimeframe,
        onSuccess: handleDataUpdate,
        userRoles,
    });

    return (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
            <MaterialReactTable
                enableTopToolbar
                enableStickyHeader
                state={{ columnFilters }}
                onColumnFiltersChange={setColumnFilters}
                muiTableContainerProps={{
                    sx: {
                        "& thead tr:first-of-type th:first-of-type": {
                            paddingLeft: "20px",
                        },
                        "& tbody tr": {
                            height: "auto",
                            minHeight: "48px",
                        },
                        "& tbody tr td:first-of-type": {
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            height: "auto",
                            minHeight: "48px",
                            padding: "12px 16px",
                            verticalAlign: "top",
                        },
                        "& tbody tr td:first-of-type p": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            margin: 0,
                            lineHeight: "1.4",
                        },
                        "& tbody tr:first-of-type td > p": {
                            justifyContent: "center",
                        },
                        "& thead tr:first-of-type th:nth-of-type(2)": {
                            paddingLeft: "16px",
                        },
                        "& tbody tr td:nth-of-type(2)": {
                            padding: "12px 16px",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            height: "auto",
                            minHeight: "48px",
                            verticalAlign: "top",
                        },
                        "& tbody tr td:nth-of-type(2) p": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            margin: 0,
                            lineHeight: "1.4",
                        },
                        "& tbody tr td": {
                            borderRight: isTableEmpty ? "none" : isEditMode ? "1px solid #191FDB" : "1px solid #C0D0D4",
                            borderBottom: isTableEmpty ? "none" : isEditMode ? "1px solid #191FDB" : "1px solid #C0D0D4",
                        },
                        border: isEditMode ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        maxHeight: "664px",
                        height: "auto",
                        minHeight: "300px",
                        width: "calc(100vw - 148px)",
                        overflow: "auto",
                    },
                }}
                muiTablePaperProps={{
                    sx: {
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                        boxShadow: "none",
                    },
                }}
                muiTopToolbarProps={{
                    sx: {
                        height: '1px',
                        minHeight: '1px',
                        backgroundColor: 'transparent',
                        '& .MuiIconButton-root': {
                            display: 'none',
                        },
                    },
                }}
                muiBottomToolbarProps={{
                    sx: {
                        display: "none",
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        lineHeight: "30px",
                        paddingRight: "16px",
                        "& .MuiBadge-root": {
                            display: "none",
                        },
                        borderRight: isEditMode ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                        borderBottom: isEditMode ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        display: 'flex',
                        flexWrap: 'wrap',
                        background: 'transparent',
                    }
                }}
                muiTableHeadRowProps={{
                    sx: {
                        "& > th:first-of-type": {
                            paddingLeft: "20px",
                        },
                        "& > th": {
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingLeft: '12px',
                            paddingRight: '16px',
                            borderRight: isEditMode ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                            borderBottom: isEditMode ? "1px solid #191FDB !important" : "1px solid #C0D0D4 !important",
                        },
                        height: "auto",
                        backgroundColor: "#F8FAFC",
                    },
                }}
                muiTableBodyRowProps={({ row }) => ({
                    sx: {
                        backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                            ? "#e8f5e1"
                            : "white",
                        height: "auto",
                        minHeight: "48px",
                        "&:hover": {
                            backgroundColor: (row.original as ExtendedScorecardDataItem).isCostRow
                                ? "#e8f5e1"
                                : "#f5f5f5",
                        },
                    },
                })}
                columns={columns}
                data={processedData}
                enablePagination={false}
                enableRowVirtualization
                enableColumnResizing
                enablePinning
                initialState={{
                    density: 'compact',
                    columnPinning: { left: getScorecardPinnedLeftColumnIds(isAdmin) },
                }}
            />
        </Box>
    );
};